import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Layout, { PageHeader } from "../components/Layout";
import { api } from "../lib/api";
import { Search, ClipboardCheck, MapPin } from "lucide-react";
import { CATEGORY_LABELS, CATEGORY_ORDER } from "../lib/categories";
import useLiveSync from "../lib/useLiveSync";
import FlavorQuickAdd, { FLAVOR_ADDABLE_CATEGORIES } from "../components/FlavorQuickAdd";
import { useAuth } from "../context/AuthContext";
import { unitText } from "../lib/stock";

const BLISTER_CATEGORIES = new Set(["macaron", "macaron_seasonal"]);
const FULL_TRAY = 70;

/**
 * Franchisee **My Inventory** — read-only live view of this store's on-hand.
 *
 * Stock moves in two ways:
 *  (1) An order is marked Picked Up → items auto-add here (and deduct from
 *      Kitchen) — handled server-side.
 *  (2) The franchisee submits the End-of-Day Count — their inventory is set to
 *      exactly what they report.
 *
 * There is nothing to type on this page. Use "End-of-Day Count" to update counts.
 */
export default function MyInventory() {
  const { user } = useAuth();
  const locationName = user?.franchise_name || user?.location_name || "your store";
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [loaded, setLoaded] = useState(false);
  const sectionRefs = useRef({});

  const refresh = () => api.get("/inventory")
    .then((r) => {
      // Safety net: deduplicate by id in case live-sync races a refresh and
      // momentarily double-renders. Also hard-filter to the user's location
      // so a franchisee can never see cross-store rows regardless of what
      // the backend returned.
      const seen = new Set();
      const scoped = r.data
        .filter((it) => !user?.location_id || it.location_id === user.location_id)
        .filter((it) => (seen.has(it.id) ? false : seen.add(it.id)));
      setItems(scoped);
      setLoaded(true);
    })
    .catch(() => setLoaded(true));

  useEffect(() => { refresh(); }, []);
  // Live-sync: pickup auto-transfers from Kitchen reflect here within a few seconds
  useLiveSync(refresh);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return items.filter((i) => !q || i.name.toLowerCase().includes(q));
  }, [items, query]);

  const grouped = useMemo(() => {
    const buckets = {};
    for (const it of filtered) (buckets[it.category] ||= []).push(it);
    const keys = [
      ...CATEGORY_ORDER.filter((k) => buckets[k]),
      ...Object.keys(buckets).filter((k) => !CATEGORY_ORDER.includes(k)),
    ];
    return keys.map((k) => ({
      key: k, label: CATEGORY_LABELS[k] || k,
      rows: buckets[k].sort((a, b) => a.name.localeCompare(b.name)),
    }));
  }, [filtered]);

  const totals = useMemo(() => {
    const total = items.reduce((s, i) => s + (i.current_stock || 0), 0);
    const low = items.filter((i) => i.current_stock <= (i.low_stock_threshold || 0)).length;
    return { total, low, count: items.length };
  }, [items]);

  const jumpTo = (k) => {
    const el = sectionRefs.current[k];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Layout>
      <PageHeader
        eyebrow="My Store"
        title="My Inventory"
        description="Live view of what's on your shelves right now. Picked-up orders add here automatically. Use End-of-Day Count to update the counts."
        actions={
          <Link
            to="/daily-report"
            data-testid="my-inv-open-daily-log"
            className="h-11 px-5 rounded-xl bg-foreground text-background text-sm font-medium hover:bg-foreground/90 inline-flex items-center gap-2"
          >
            <ClipboardCheck className="h-4 w-4" />
            Open End-of-Day Count
          </Link>
        }
      />

      <div
        className="mb-6 flex items-center gap-3 rounded-xl border border-border bg-secondary/40 px-4 py-3 text-sm text-muted-foreground"
        data-testid="my-inv-source-note"
      >
        <MapPin className="h-4 w-4 text-foreground" />
        <span>
          Showing inventory for <b className="text-foreground">{locationName}</b> only. Picked-up orders add here automatically. Use <b className="text-foreground">End-of-Day Count</b> to update the counts.
        </span>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mb-6" data-testid="my-inventory-summary">
        <Stat label="Items tracked" value={totals.count} />
        <Stat label="Units on hand" value={totals.total} />
        <Stat label="Low stock" value={totals.low} accent={totals.low > 0 ? "destructive" : "neutral"} />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search all categories…"
            data-testid="my-inv-search"
            className="h-11 pl-9 pr-3 rounded-xl border border-input bg-card text-sm w-72"
          />
        </div>
        <span className="text-xs text-muted-foreground">
          {loaded ? `${filtered.length} items` : "Loading…"}
        </span>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap" data-testid="my-inv-category-pills">
        {grouped.map((g) => (
          <button
            key={g.key}
            onClick={() => jumpTo(g.key)}
            data-testid={`my-inv-jump-${g.key}`}
            className="h-9 px-4 rounded-full text-xs font-medium bg-card border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
          >
            {g.label} <span className="text-[10px] opacity-60">· {g.rows.length}</span>
          </button>
        ))}
      </div>

      <div className="space-y-8" data-testid="my-inv-groups">
        {loaded && grouped.length === 0 && (
          <div className="luxury-card p-12 text-center text-sm text-muted-foreground">
            {items.length === 0 ? "Your inventory is empty. Pick up an order to receive stock from the Kitchen." : "No items match your search."}
          </div>
        )}
        {grouped.map((g) => {
          const isBlister = BLISTER_CATEGORIES.has(g.key);
          return (
            <section
              key={g.key}
              ref={(el) => { sectionRefs.current[g.key] = el; }}
              data-testid={`my-inv-section-${g.key}`}
            >
              <h2 className="font-display text-xl font-medium tracking-tight mb-3 flex items-baseline gap-3">
                {g.label}
                <span className="text-xs font-normal text-muted-foreground uppercase tracking-wider">
                  {g.rows.length} item{g.rows.length !== 1 ? "s" : ""}
                </span>
              </h2>
              <div className="luxury-card overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/60">
                    <tr className="text-left">
                      <th className="px-5 py-3 label-eyebrow font-medium">Item</th>
                      <th className="px-5 py-3 label-eyebrow font-medium">Pack</th>
                      {isBlister ? (
                        <>
                          <th className="px-5 py-3 label-eyebrow font-medium text-right w-24">Full × 70</th>
                          <th className="px-5 py-3 label-eyebrow font-medium text-right w-24">Partial</th>
                          <th className="px-5 py-3 label-eyebrow font-medium text-right w-28">On Hand</th>
                        </>
                      ) : (
                        <th className="px-5 py-3 label-eyebrow font-medium text-right w-28">On Hand</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {g.rows.map((it) => (
                      <InventoryRow key={it.id} item={it} isBlister={isBlister} />
                    ))}
                  </tbody>
                </table>
              </div>
              {FLAVOR_ADDABLE_CATEGORIES.has(g.key) && (
                <FlavorQuickAdd
                  category={g.key}
                  categoryLabel={g.label}
                  onSaved={refresh}
                />
              )}
            </section>
          );
        })}
      </div>
    </Layout>
  );
}


function InventoryRow({ item, isBlister }) {
  const stock = item.current_stock || 0;
  const low = stock <= (item.low_stock_threshold || 0);
  const full = isBlister ? Math.floor(stock / FULL_TRAY) : null;
  const partial = isBlister ? stock % FULL_TRAY : null;
  return (
    <tr className="border-t border-border align-top" data-testid={`my-inv-row-${item.id}`}>
      <td className="px-5 py-3">
        <div className="font-medium">{item.name}</div>
        {item.flavor && item.flavor !== item.name && (
          <div className="text-xs text-muted-foreground mt-0.5">· {item.flavor}</div>
        )}
      </td>
      <td className="px-5 py-3 text-muted-foreground">
        {unitText(item)}
      </td>
      {isBlister ? (
        <>
          <td className="px-5 py-3 text-right text-muted-foreground tabular-nums">{full}</td>
          <td className="px-5 py-3 text-right text-muted-foreground tabular-nums">{partial}</td>
          <td
            className={`px-5 py-3 text-right font-display text-lg tabular-nums ${low ? "text-destructive" : "text-foreground"}`}
            data-testid={`my-inv-stock-${item.id}`}
          >
            {stock}
          </td>
        </>
      ) : (
        <td
          className={`px-5 py-3 text-right font-display text-lg tabular-nums ${low ? "text-destructive" : "text-foreground"}`}
          data-testid={`my-inv-stock-${item.id}`}
        >
          {stock}
        </td>
      )}
    </tr>
  );
}


function Stat({ label, value, accent }) {
  const tone = accent === "destructive" ? "text-destructive" : "text-foreground";
  return (
    <div className="luxury-card p-5">
      <div className="label-eyebrow mb-1">{label}</div>
      <div className={`font-display text-3xl font-light tabular-nums ${tone}`}>{value}</div>
    </div>
  );
}
